/**
 * StrategiesWidget — Strategy tracking, performance metrics, and portfolio optimization
 * CSS prefix: stw-
 */

import { Target, TrendingUp, BarChart3, Search, Lock } from 'lucide-react'
import './StrategiesWidget.css'

const TAB_CONTENT = {
  'strategy-tracker': {
    title: 'Strategy Tracker',
    icon: Target,
    description: 'Define, track, and monitor betting strategies with institutional-grade metrics',
    features: [
      'Steam chasing — Limit increases with collapsing odds detection',
      'Closing line value (CLV) — Track if you consistently beat the closing line',
      'Opening line value — Capture early market inefficiencies',
      '+EV accumulator — Automated positive expected value bet tracking',
      'Arbitrage capture — Cross-book guaranteed profit strategies',
      'Contrarian plays — Fade public consensus, follow sharp money',
    ],
  },
  'performance': {
    title: 'Performance Analytics',
    icon: TrendingUp,
    description: 'Track strategy performance with financial-grade metrics',
    features: [
      'ROI — Return on investment by strategy, sport, market',
      'Sharpe Ratio — Risk-adjusted returns (annualized)',
      'Sortino Ratio — Downside risk-adjusted returns',
      'Max Drawdown — Largest peak-to-trough decline',
      'Win Rate / CLV — Winning percentage and closing line value',
      'Equity Curves — Cumulative P&L charts with drawdown overlay',
      'Kelly Criterion — Optimal bet sizing with fractional Kelly options',
      'Volatility — Rolling annualized volatility of returns',
      'Correlation Matrix — Strategy return correlations',
    ],
  },
  'efficient-frontier': {
    title: 'Efficient Frontier',
    icon: BarChart3,
    description: 'Modern Portfolio Theory applied to sports betting',
    features: [
      'Efficient frontier visualization for all available bets',
      'Risk-return scatter plot with optimal portfolio line',
      'Minimum variance portfolio identification',
      'Maximum Sharpe ratio portfolio',
      'Correlation-based diversification analysis',
      'Parlay optimization along the frontier',
      'European option payoff structure analysis (bets as options)',
      'American option extensions for cash-out scenarios',
    ],
  },
  'screener': {
    title: 'Bet Screener',
    icon: Search,
    description: 'Finviz-style screener for filtering and ranking available bets',
    features: [
      'Filter by sport, league, market, book, odds range',
      'Sort by EV%, hold%, line movement, steam score',
      'Columns: odds, fair value, edge, kelly size, CLV history',
      'Heatmap view for quick visual scanning',
      'Watchlist with alerts for threshold triggers',
      'Export to CSV for external analysis',
    ],
  },
}

export default function StrategiesWidget({ tab = 'strategy-tracker' }) {
  const content = TAB_CONTENT[tab] || TAB_CONTENT['strategy-tracker']
  const Icon = content.icon

  return (
    <div className="widget stw-widget">
      <div className="widget-header">
        <Icon size={16} />
        <h2 className="widget-title">{content.title}</h2>
        <span className="stw-badge">COMING SOON</span>
      </div>

      <div className="stw-body">
        <div className="stw-hero">
          <Icon size={48} className="stw-hero-icon" />
          <h3 className="stw-hero-title">{content.title}</h3>
          <p className="stw-hero-desc">{content.description}</p>
        </div>

        <div className="stw-features">
          <h4 className="stw-features-title">Planned Features</h4>
          {content.features.map((feat, i) => (
            <div key={i} className="stw-feature">
              <span className="stw-feature-bullet">→</span>
              <span className="stw-feature-text">{feat}</span>
            </div>
          ))}
        </div>

        <div className="stw-cta">
          <Lock size={14} />
          <span>This module is under active development. Expected Q2 2026.</span>
        </div>
      </div>
    </div>
  )
}
